import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from './AuthContext';
import {
  clearPendingReloadInfo,
  currentBuildInfo,
  fetchDeployedVersion,
  getSafeSessionStorage,
  isChunkLoadFailure,
  PendingReloadInfo,
  readPendingReloadInfo,
  shouldReloadForVersion,
  writePendingReloadInfo,
} from '../services/appUpdate';
import { IS_PRODUCTION_BUILD } from '../services/buildInfo';
import { logEventAsync } from '../services/logger';

const VERSION_POLL_MS = 60000;
const BEFORE_RELOAD_TIMEOUT_MS = 10000;

interface ReloadRequest {
  reason: string;
  toBuildId: string;
}

interface AppUpdateContextValue {
  isRefreshing: boolean;
  registerBeforeReload: (handler: () => Promise<void>) => () => void;
}

const DEFAULT: AppUpdateContextValue = {
  isRefreshing: false,
  registerBeforeReload: () => () => {},
};

const Ctx = createContext<AppUpdateContextValue>(DEFAULT);

type UpdatePhase = 'idle' | 'saving' | 'reloading' | 'blocked';

export function AppUpdateProvider({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();
  const handlersRef = useRef(new Map<number, () => Promise<void>>());
  const nextHandlerIdRef = useRef(1);
  const reloadInFlightRef = useRef(false);
  const loggedBuildKeysRef = useRef(new Set<string>());
  const [phase, setPhase] = useState<UpdatePhase>('idle');
  const [blockedRequest, setBlockedRequest] = useState<ReloadRequest | null>(null);

  const registerBeforeReload = useCallback((handler: () => Promise<void>) => {
    const id = nextHandlerIdRef.current;
    nextHandlerIdRef.current += 1;
    handlersRef.current.set(id, handler);
    return () => {
      handlersRef.current.delete(id);
    };
  }, []);

  const runBeforeReloadHandlers = useCallback(async () => {
    for (const handler of Array.from(handlersRef.current.values())) {
      await Promise.race([
        handler(),
        new Promise((_, reject) => {
          window.setTimeout(
            () => reject(new Error('Timed out waiting for pre-reload save.')),
            BEFORE_RELOAD_TIMEOUT_MS,
          );
        }),
      ]);
    }
  }, []);

  const performReload = useCallback(async (request: ReloadRequest) => {
    if (!IS_PRODUCTION_BUILD || typeof window === 'undefined') return;
    if (reloadInFlightRef.current) return;
    reloadInFlightRef.current = true;
    setBlockedRequest(null);
    setPhase('saving');

    const current = currentBuildInfo();
    const baseContext = {
      fromBuildId: current.buildId,
      toBuildId: request.toBuildId,
      reason: request.reason,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    await logEventAsync('NOTICE', 'frontend_build_stale_detected', baseContext);

    try {
      await runBeforeReloadHandlers();
    } catch (err) {
      reloadInFlightRef.current = false;
      setBlockedRequest(request);
      setPhase('blocked');
      await logEventAsync('WARNING', 'frontend_build_reload_blocked', {
        ...baseContext,
        error: err instanceof Error ? err.message : String(err),
      });
      return;
    }

    const pendingReload: PendingReloadInfo = {
      fromBuildId: current.buildId,
      toBuildId: request.toBuildId,
      reason: request.reason,
      detectedAt: new Date().toISOString(),
    };
    writePendingReloadInfo(getSafeSessionStorage(), pendingReload);

    setPhase('reloading');
    await logEventAsync('NOTICE', 'frontend_build_reloading', baseContext);
    window.location.reload();
  }, [runBeforeReloadHandlers]);

  const checkForUpdate = useCallback(async () => {
    if (!IS_PRODUCTION_BUILD || typeof window === 'undefined') return;
    if (reloadInFlightRef.current) return;

    try {
      const deployed = await fetchDeployedVersion();
      if (!shouldReloadForVersion(currentBuildInfo(), deployed)) return;
      await performReload({
        reason: 'version-mismatch',
        toBuildId: deployed!.buildId,
      });
    } catch {
      // Retry on the next poll/focus pass.
    }
  }, [performReload]);

  useEffect(() => {
    if (!IS_PRODUCTION_BUILD || typeof window === 'undefined') return undefined;

    void checkForUpdate();

    const intervalId = window.setInterval(() => {
      void checkForUpdate();
    }, VERSION_POLL_MS);

    const onFocus = () => {
      void checkForUpdate();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    if (!IS_PRODUCTION_BUILD || typeof window === 'undefined') return undefined;

    const onError = (event: Event) => {
      if (!isChunkLoadFailure(event)) return;
      void performReload({
        reason: 'chunk-load-error',
        toBuildId: 'unknown',
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadFailure(event.reason)) return;
      event.preventDefault();
      void performReload({
        reason: 'chunk-load-error',
        toBuildId: 'unknown',
      });
    };

    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [performReload]);

  useEffect(() => {
    if (!IS_PRODUCTION_BUILD || typeof window === 'undefined') return;
    if (loading || !currentUser) return;

    const build = currentBuildInfo();
    const buildKey = `${currentUser.uid}:${build.buildId}`;
    if (!loggedBuildKeysRef.current.has(buildKey)) {
      loggedBuildKeysRef.current.add(buildKey);
      void logEventAsync('INFO', 'frontend_build_loaded', {
        buildId: build.buildId,
        builtAt: build.builtAt,
        gitSha: build.gitSha,
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    }

    const storage = getSafeSessionStorage();
    const pending = readPendingReloadInfo(storage);
    if (!pending || pending.toBuildId !== build.buildId) return;

    clearPendingReloadInfo(storage);
    void logEventAsync('NOTICE', 'frontend_build_reload_complete', {
      fromBuildId: pending.fromBuildId,
      toBuildId: pending.toBuildId,
      reason: pending.reason,
      detectedAt: pending.detectedAt,
      url: window.location.href,
    });
  }, [currentUser, loading]);

  const value = useMemo<AppUpdateContextValue>(() => ({
    isRefreshing: phase === 'saving' || phase === 'reloading',
    registerBeforeReload,
  }), [phase, registerBeforeReload]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {phase !== 'idle' && (
        <UpdateOverlay
          phase={phase}
          onRetry={blockedRequest ? () => void performReload(blockedRequest) : undefined}
          onDismiss={phase === 'blocked' ? () => {
            setBlockedRequest(null);
            setPhase('idle');
          } : undefined}
        />
      )}
    </Ctx.Provider>
  );
}

export function useAppUpdate(): AppUpdateContextValue {
  return useContext(Ctx);
}

function UpdateOverlay({
  phase,
  onRetry,
  onDismiss,
}: {
  phase: UpdatePhase;
  onRetry?: () => void;
  onDismiss?: () => void;
}) {
  const saving = phase === 'saving';
  const blocked = phase === 'blocked';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/50 px-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          {blocked ? (
            <span className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
            </span>
          ) : (
            <span className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5 animate-spin" />}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-900">
              {blocked ? 'Update paused' : 'Updating app'}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {blocked
                ? 'A new version is ready, but the app could not save your latest task changes automatically. Save your work, then refresh.'
                : saving
                  ? 'A new version is available. Saving your work before refreshing…'
                  : 'Refreshing to the latest version…'}
            </p>
            {blocked && (
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onDismiss}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Later
                </button>
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
